import { TFunction } from 'react-i18next'
import { ActionPlanTypeEvidence } from '../../../service/actionPlans/actionPlanById'
import { AnswerActionPlanImplementedBody } from '../../../service/actionPlans/body'
import { EvidencesTypeValueEnum } from '../../../../utils/enums/evidences'
import { SolveActionPlanPayload, SolverEvidence, SolverEvidenceType } from './types'

const EVIDENCE_TYPE_ID_TO_KEY: Record<number, SolverEvidenceType> = {
  [EvidencesTypeValueEnum.IMAGE]: 'image',
  [EvidencesTypeValueEnum.CAMERA]: 'camera',
  [EvidencesTypeValueEnum.VIDEO]: 'video',
  [EvidencesTypeValueEnum.VIDEO_GALLERY]: 'video_gallery',
  [EvidencesTypeValueEnum.SIGN]: 'sign',
  [EvidencesTypeValueEnum.DOCUMENT]: 'document',
  [EvidencesTypeValueEnum.COMMENT]: 'comment',
}

const mapEvidenceTypeIdToKey = (evidenceTypeId?: number): SolverEvidenceType | null => {
  if (evidenceTypeId === undefined || evidenceTypeId === null) return null
  return EVIDENCE_TYPE_ID_TO_KEY[evidenceTypeId] ?? null
}

export const mapActionPlanTypeEvidences = (
  typeEvidences: ActionPlanTypeEvidence[] | undefined,
  t: TFunction<'translation', undefined>
): SolverEvidence[] => {
  if (!typeEvidences || typeEvidences.length === 0) return []

  return typeEvidences
    .filter((typeEvidence) => typeEvidence.type === 'SOLVER')
    .map((typeEvidence) => {
      const key = mapEvidenceTypeIdToKey(typeEvidence.evidenceTypeId)
      const fallbackKey = key ?? 'image'

      return {
        id: fallbackKey,
        label: `actionPlan.solveActionPlan.types.${fallbackKey}`,
        evidenceTypeId: typeEvidence.evidenceTypeId ?? 0,
        files: [],
        comments: [],
        required: typeEvidence.required ?? false,
        maxAmount: typeEvidence.paramsConfiguration?.amounts ?? 0,
        allowedTypes: typeEvidence.paramsConfiguration?.types ?? [],
        maxSizeMB: typeEvidence.paramsConfiguration?.sizes ?? 0,
      }
    })
}

export const isValidEvidenceFile = (
  file: File,
  evidence: SolverEvidence
): { valid: boolean; error?: string } => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''

  if (evidence.allowedTypes.length > 0 && !evidence.allowedTypes.includes(extension)) {
    return { valid: false, error: 'invalidType' }
  }

  if (evidence.maxSizeMB > 0 && file.size > evidence.maxSizeMB * 1024 * 1024) {
    return { valid: false, error: 'maxSize' }
  }

  return { valid: true }
}

export const buildSolvePayload = (
  actionPlanImplementedId: number | undefined,
  evidences: SolverEvidence[]
): SolveActionPlanPayload => ({
  actionPlanImplementedId,
  evidences,
})

export const buildAnswerActionPlanPayload = (
  actionPlanAppliedId: number | undefined,
  comment: string,
  evidences: SolverEvidence[]
): AnswerActionPlanImplementedBody | null => {
  if (!actionPlanAppliedId) return null

  const payloadEvidences: AnswerActionPlanImplementedBody['evidences'] = []
  const comments: string[] = []

  evidences.forEach((evidence) => {
    if (evidence.id === 'comment') {
      comments.push(...evidence.comments)
      return
    }

    evidence.files.forEach((file) => {
      if (!file.url || file.isUploading || file.error) return
      payloadEvidences.push({
        url: file.url,
        originalname: file.name,
        evidenceTypeId: file.evidenceTypeId,
      })
    })
  })

  return {
    actionPlanAppliedId,
    comment,
    comments,
    evidences: payloadEvidences,
  }
}
